import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Send, Trash2 } from 'lucide-react';
import {
  NotificationsApi,
  NotificationSendResult,
  NotificationType,
  SettingsApi,
  NotificationCategory,
  NotificationSettings,
  NOTIFICATION_CATEGORIES,
} from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  attendance_absent: 'Attendance (absent marked)',
  fee_payment: 'Fee payment received',
  exam_result: 'Exam result published',
  leave_status: 'Leave approved/rejected',
  tc_issued: 'Transfer certificate issued',
  assignment_published: 'New assignment posted',
  announcement: 'Announcements',
};

const AUDIENCE_LABEL: Record<NotificationType, string> = {
  all: 'All users',
  specific: 'Specific users',
  roles: 'Roles',
  overdue_fees: 'Overdue Fees',
  specific_class: 'Specific Class',
};

const TYPE_TONE: Record<NotificationType, 'slate' | 'blue' | 'indigo' | 'amber'> = {
  all: 'blue',
  specific: 'indigo',
  roles: 'slate',
  overdue_fees: 'amber',
  specific_class: 'indigo',
};

const ROLE_OPTIONS = ['owner', 'admin', 'manager', 'teacher', 'staff', 'cashier', 'student', 'parent'];

const composeSchema = z.object({
  title: z.string().min(1, 'Required').max(255),
  message: z.string().min(1, 'Required').max(2000),
  type: z.enum(['all', 'specific', 'roles', 'overdue_fees', 'specific_class']),
});
type ComposeForm = z.infer<typeof composeSchema>;

const TABS = ['compose', 'settings'] as const;
type Tab = (typeof TABS)[number];

export function NotificationsPage() {
  const { can, isAdmin } = usePermissions();
  const canWrite = can('/notifications', 'create');
  const canDelete = can('/notifications', 'delete');
  const [tab, setTab] = useState<Tab>('compose');

  return (
    <>
      <PageHeader
        title="Notifications"
        description="Compose and send push notifications, review dispatch history, and control which automatic events notify users."
      />
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium capitalize',
              tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'compose' ? (
        <>
          {canWrite && <ComposeCard />}
          <HistoryTable canDelete={canDelete} />
        </>
      ) : (
        <SettingsCard readOnly={!isAdmin} />
      )}
    </>
  );
}

// ───── Settings ─────────────────────────────────────────────────────────────
function SettingsCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: SettingsApi.getNotificationSettings,
  });

  const save = useMutation({
    mutationFn: (next: Partial<NotificationSettings>) => SettingsApi.setNotificationSettings(next),
    onSuccess: (next) => qc.setQueryData(['notification-settings'], next),
  });

  if (isLoading || !data) {
    return <div className="card p-8 text-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">Automatic notifications</h2>
      <p className="mb-4 text-sm text-slate-500">
        Turn off any event below to stop it from notifying users — the event itself still happens, it just won't send a notification.
      </p>
      <div className="divide-y divide-slate-100">
        {NOTIFICATION_CATEGORIES.map((cat) => (
          <label key={cat} className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-sm text-slate-700">{CATEGORY_LABEL[cat]}</span>
            <input
              type="checkbox"
              checked={data[cat] !== false}
              disabled={readOnly || save.isPending}
              onChange={(e) => save.mutate({ [cat]: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
            />
          </label>
        ))}
      </div>
      {readOnly && <p className="mt-3 text-xs text-slate-400">Only admins can change these settings.</p>}
    </div>
  );
}

// ───── Compose ──────────────────────────────────────────────────────────────
function ComposeCard() {
  const qc = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [result, setResult] = useState<NotificationSendResult | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ComposeForm>({
    resolver: zodResolver(composeSchema),
    defaultValues: { type: 'all' },
  });
  const type = watch('type');

  const { data: users = [] } = useQuery({
    queryKey: ['notification-users'],
    queryFn: NotificationsApi.lookupUsers,
    enabled: type === 'specific',
  });
  const filteredUsers = useMemo(
    () => users.filter((u) => u.name.toLowerCase().includes(userSearch.toLowerCase())),
    [users, userSearch],
  );

  const { data: sections = [] } = useQuery({
    queryKey: ['notification-sections'],
    queryFn: NotificationsApi.lookupSections,
    enabled: type === 'specific_class',
  });

  const create = useMutation({
    mutationFn: (f: ComposeForm) =>
      NotificationsApi.create({
        title: f.title,
        message: f.message,
        type: f.type,
        userIds: f.type === 'specific' ? [...selectedUserIds] : undefined,
        roles: f.type === 'roles' ? [...selectedRoles] : undefined,
        sectionIds: f.type === 'specific_class' ? [...selectedSections] : undefined,
      }),
    onSuccess: (res) => {
      setResult(res);
      reset({ title: '', message: '', type: 'all' });
      setSelectedUserIds(new Set());
      setSelectedRoles(new Set());
      setSelectedSections(new Set());
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const toggleUser = (id: string) =>
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleRole = (role: string) =>
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      next.has(role) ? next.delete(role) : next.add(role);
      return next;
    });
  const toggleSection = (id: string) =>
    setSelectedSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const canSubmit =
    (type !== 'specific' || selectedUserIds.size > 0) &&
    (type !== 'roles' || selectedRoles.size > 0) &&
    (type !== 'specific_class' || selectedSections.size > 0);

  return (
    <div className="card mb-6 p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">Compose</h2>
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
          <Input placeholder="e.g. Fee reminder" {...register('title')} />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
          <Textarea rows={3} placeholder="Notification body" {...register('message')} />
          {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Audience</label>
          <Select {...register('type')}>
            {(Object.keys(AUDIENCE_LABEL) as NotificationType[]).map((t) => (
              <option key={t} value={t}>
                {AUDIENCE_LABEL[t]}
              </option>
            ))}
          </Select>
        </div>

        {type === 'specific' && (
          <div className="rounded-md border border-slate-200 p-3">
            <Input
              className="mb-2"
              placeholder="Search users…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {filteredUsers.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedUserIds.has(u.id)} onChange={() => toggleUser(u.id)} />
                  <span className="text-slate-700">{u.name}</span>
                  <span className="text-xs capitalize text-slate-400">{u.role}</span>
                </label>
              ))}
              {filteredUsers.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">No users found.</p>}
            </div>
            <p className="mt-2 text-xs text-slate-500">{selectedUserIds.size} selected</p>
          </div>
        )}

        {type === 'roles' && (
          <div className="rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap gap-3">
              {ROLE_OPTIONS.map((role) => (
                <label key={role} className="flex cursor-pointer items-center gap-1.5 text-sm capitalize">
                  <input type="checkbox" checked={selectedRoles.has(role)} onChange={() => toggleRole(role)} />
                  {role}
                </label>
              ))}
            </div>
          </div>
        )}

        {type === 'specific_class' && (
          <div className="rounded-md border border-slate-200 p-3">
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {sections.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedSections.has(s.id)} onChange={() => toggleSection(s.id)} />
                  <span className="text-slate-700">{s.name}</span>
                </label>
              ))}
              {sections.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">No classes found.</p>}
            </div>
            <p className="mt-2 text-xs text-slate-500">{selectedSections.size} selected</p>
          </div>
        )}

        {type === 'overdue_fees' && (
          <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            Notifies every student with an overdue, unpaid fee demand (due date already passed), plus their linked guardian(s).
          </div>
        )}

        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={create.isPending || !canSubmit}>
            <Send className="mr-1.5 h-4 w-4" />
            {create.isPending ? 'Sending…' : 'Send notification'}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="text-slate-700">
            Targeted <span className="font-medium">{result.recipientCount}</span> recipient
            {result.recipientCount === 1 ? '' : 's'} — <span className="font-medium">{result.reachableCount}</span> with a
            registered device.
          </div>
          {result.send.attempted ? (
            <div className="mt-1 text-green-700">
              Delivered to {result.send.successCount ?? 0} device{result.send.successCount === 1 ? '' : 's'}
              {(result.send.failureCount ?? 0) > 0 ? `, ${result.send.failureCount} failed` : ''}.
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-1.5 text-slate-500">
              <Info className="h-3.5 w-3.5" />
              Push delivery pending Firebase setup — the notification is recorded and recipients are resolved, but no device push was sent yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───── History ──────────────────────────────────────────────────────────────
function HistoryTable({ canDelete }: { canDelete: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => NotificationsApi.list({ limit: 50 }),
  });
  const items = data?.items ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => NotificationsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">History</h2>
      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No notifications sent yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Title</Th>
                <Th>Audience</Th>
                <Th>Recipients</Th>
                <Th>Sent by</Th>
                <Th>Sent at</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{n.title}</div>
                    <div className="max-w-md truncate text-xs text-slate-500">{n.message}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={TYPE_TONE[n.type]}>{AUDIENCE_LABEL[n.type]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{n.recipientCount}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{n.sentByName ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{new Date(n.sentAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    {canDelete && (
                      <button
                        className="text-slate-400 hover:text-red-600"
                        onClick={() => remove.mutate(n.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className={cn('px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500')}>{children}</th>
  );
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
